import { debounce } from '../utils/utilities.js';

const SELECTORS = {
    block: '.wp-block-ssm-accordion',
    item: '.wp-block-ssm-accordion-item',
    trigger: '[data-accordion-trigger]',
    triggerInner: '.wp-block-ssm-accordion__trigger-inner',
    panel: '[data-accordion-panel]',
};

const CLASSES = {
    open: 'is-open',
};

const PANEL_MARGIN = 50;
const MIN_EXPANDABLE_HEIGHT = 380;
const MIN_CALCULATED_HEIGHT = 150; // Threshold below which we use MIN_EXPANDABLE_HEIGHT
const RESIZE_DEBOUNCE_MS = 200;

/**
 * Calculate available height for expanded panel content.
 * Batches all DOM reads before writes to avoid layout thrashing.
 *
 * @param {HTMLElement} block - The accordion container
 * @param {Object} cache - Cached element references
 */
const calculateExpandableHeight = (block, cache) => {
    // Single read phase - batch all offsetHeight calls
    let totalTriggerHeight = 0;
    let openPanelHeight = 0;

    cache.triggerInners.forEach((inner) => {
        totalTriggerHeight += inner.offsetHeight;
    });

    // Border calculation uses cached item count
    const borderHeight = cache.itemCount > 0 ? 2 + (cache.itemCount - 1) * 2 : 0;
    totalTriggerHeight += borderHeight;

    const blockHeight = block.offsetHeight;

    cache.items.forEach((item) => {
        if (item.classList.contains(CLASSES.open)) {
            const panel = cache.panelMap.get(item);
            if (panel) {
                openPanelHeight += panel.offsetHeight;
            }
        }
    });

    // Single write phase
    const collapsedBlockHeight = blockHeight - openPanelHeight;
    let expandableHeight = collapsedBlockHeight - totalTriggerHeight - PANEL_MARGIN;

    if (expandableHeight < MIN_CALCULATED_HEIGHT) {
        expandableHeight = MIN_EXPANDABLE_HEIGHT;
    }

    // Only update if value changed
    if (cache.lastExpandableHeight !== expandableHeight) {
        cache.lastExpandableHeight = expandableHeight;
        block.style.setProperty('--expandable-content-height', `${expandableHeight}px`);
    }

    return expandableHeight;
};

const setItemState = (item, isOpen, cache) => {
    if (!item || !cache) return;

    const trigger = cache.triggerMap.get(item);
    const panel = cache.panelMap.get(item);

    item.classList.toggle(CLASSES.open, isOpen);
    item.dataset.open = isOpen ? 'true' : 'false';

    if (trigger) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    if (panel) {
        panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

        // Toggle inert attribute based on open/closed state
        if (isOpen) {
            panel.removeAttribute('inert');
        } else {
            panel.setAttribute('inert', '');
        }
    }
};

const buildCache = (block, items) => {
    const triggerInners = Array.from(block.querySelectorAll(SELECTORS.triggerInner));
    const triggerMap = new Map();
    const panelMap = new Map();

    items.forEach((item) => {
        const trigger = item.querySelector(SELECTORS.trigger);
        const panel = item.querySelector(SELECTORS.panel);
        if (trigger) triggerMap.set(item, trigger);
        if (panel) panelMap.set(item, panel);
    });

    return {
        items,
        itemCount: items.length,
        triggerInners,
        triggerMap,
        panelMap,
        lastExpandableHeight: null,
    };
};

export const initAccordion = (block) => {
    if (!block || block.dataset.accordionReady) return null;

    const items = Array.from(block.querySelectorAll(SELECTORS.item));
    if (!items.length) return null;

    const allowMultiple = block.dataset.allowMultiple === 'true';
    const openFirst = block.dataset.openFirst !== 'false';

    block.dataset.accordionReady = 'true';

    const cache = buildCache(block, items);

    // Shared state object to avoid stale closures
    const state = {
        rafId: null,
        isDestroyed: false,
    };

    // Cancels any pending RAF
    const cancelRaf = () => {
        if (state.rafId) {
            cancelAnimationFrame(state.rafId);
            state.rafId = null;
        }
    };

    // Schedule a height calculation, canceling any pending one
    const scheduleCalculation = () => {
        if (state.isDestroyed) return;

        cancelRaf();
        state.rafId = requestAnimationFrame(() => {
            if (!state.isDestroyed) {
                calculateExpandableHeight(block, cache);
            }
        });
    };

    // Debounced handler for resize events
    const handleResize = debounce(() => {
        if (!state.isDestroyed) {
            scheduleCalculation();
        }
    }, RESIZE_DEBOUNCE_MS);

    // Observe block container instead of individual triggers
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(block);

    // Initial calculation after layout is complete
    scheduleCalculation();

    const closeSiblings = (current) => {
        if (allowMultiple) return;

        cache.items.forEach((item) => {
            if (item !== current) {
                setItemState(item, false, cache);
            }
        });
    };

    const toggleItem = (item) => {
        if (state.isDestroyed) return;

        const isOpen = item.classList.contains(CLASSES.open);
        const nextState = !isOpen;

        if (nextState) {
            closeSiblings(item);
        }

        setItemState(item, nextState, cache);

        // Cancel any pending calculation and schedule new one
        scheduleCalculation();
    };

    const triggerHandlers = [];

    items.forEach((item, index) => {
        const trigger = cache.triggerMap.get(item);

        const shouldOpen = item.dataset.open === 'true' || item.classList.contains(CLASSES.open) || (openFirst && index === 0);

        setItemState(item, shouldOpen, cache);

        if (trigger) {
            const handler = () => toggleItem(item);
            trigger.addEventListener('click', handler);
            triggerHandlers.push({ trigger, handler });
        }
    });

    if (!allowMultiple) {
        const openItems = items.filter((item) => item.classList.contains(CLASSES.open));
        if (openItems.length > 1) {
            openItems.slice(1).forEach((item) => setItemState(item, false, cache));
        }
    }

    return {
        destroy() {
            // Mark as destroyed first to prevent any new async work
            state.isDestroyed = true;

            // Cancel any pending RAF
            cancelRaf();

            // Disconnect observer
            resizeObserver.disconnect();

            // Remove event listeners
            triggerHandlers.forEach(({ trigger, handler }) => {
                trigger.removeEventListener('click', handler);
            });

            // Reset DOM state
            cache.items.forEach((item) => {
                item.classList.remove(CLASSES.open);
                const panel = cache.panelMap.get(item);
                if (panel) panel.setAttribute('aria-hidden', 'true');
                const trigger = cache.triggerMap.get(item);
                if (trigger) trigger.setAttribute('aria-expanded', 'false');
            });

            block.style.removeProperty('--expandable-content-height');
            delete block.dataset.accordionReady;
        },
    };
};

export const Accordion = () => {
    document.querySelectorAll(SELECTORS.block).forEach((block) => {
        initAccordion(block);
    });
};
